import {
	Box,
	Button,
	Col,
	Divider,
	Grid,
	Icons,
	Inline,
	Input,
	joinAttributes,
	Money,
	noSpinnersClassName,
	Stack,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	arrayMutators,
	CheckboxInputField,
	Field,
	FieldArray,
	Form,
	GetFieldValue,
	PercentageInputField,
	SelectField,
	SelectInputField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	useFieldValue,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import pluralize from "pluralize"
import { useEffect, useState } from "react"
import * as Validator from "yup"
import {
	SelectTenantCurrencyInputField,
	useCurrenciesOfTenant,
	useFunctionalCurrencyOfTenant,
} from "../Currencies"
import { SelectCustomerPaymentReferences } from "./List"
import {
	customerPaymentPreferencesXHR,
	getTotalAmountShare,
	TCustomerPaymentPreference,
	useCustomerPaymentReferences,
} from "./store"
import { formatMoneyByDecimal, TCurrencyCode } from "@sembark-travel/money"
import {
	formatDayOffsetLabel,
	formatFormInstalmentLabels,
	formatFormMinFirstInstalmentAmounts,
	formatInstalmentLabel,
	getShareSegments,
	getTotalInstalmentShare,
	ruleFormValuesToPayload,
} from "./utils"

interface ICustomerPaymentPreferenceFormValues {
	days_before_trip_start: {
		is_include: boolean
		condition: string
		days: number
	}
	trip_duration: {
		is_include: boolean
		condition: string
		nights: number
	}
	package_ranges: {
		is_include: boolean
		values: [
			{
				package_range_operator: string
				package_range_currency: TCurrencyCode
				package_range_amount: number
			},
		]
	}

	min_first_instalment_amount: {
		is_include: boolean
		values: [
			{
				amount: number
				currency: TCurrencyCode
			},
		]
	}

	breakdowns: {
		reference?: { id: number; name: string }
		day_offset: number
		instalment_breakup_in_percent: number
	}[]

	is_include_flight_cost_in_first_instalment: boolean
}

const customerPaymentPreferenceSchema = Validator.object({
	/* ---------------- Days before trip start ---------------- */
	days_before_trip_start: Validator.object({
		is_include: Validator.boolean().required(),

		condition: Validator.string().when("is_include", {
			is: true,
			then: (s) => s.required("Condition is required"),
			otherwise: (s) => s.optional(),
		}),

		days: Validator.number().when("is_include", {
			is: true,
			then: (s) =>
				s
					.typeError("Days must be a number")
					.integer("Days must be an integer")
					.min(0, "Days cannot be negative")
					.required("Days are required"),
			otherwise: (s) => s.optional(),
		}),
	}),

	/* ---------------- Trip duration ---------------- */
	trip_duration: Validator.object({
		is_include: Validator.boolean().required(),

		condition: Validator.string().when("is_include", {
			is: true,
			then: (s) => s.required("Condition is required"),
			otherwise: (s) => s.optional(),
		}),

		nights: Validator.number().when("is_include", {
			is: true,
			then: (s) =>
				s
					.typeError("Nights must be a number")
					.integer("Nights must be an integer")
					.min(0, "Nights cannot be negative")
					.required("Nights are required"),
			otherwise: (s) => s.optional(),
		}),
	}),

	/* ---------------- Package ranges ---------------- */
	package_ranges: Validator.object({
		is_include: Validator.boolean().required(),

		values: Validator.array().when("is_include", {
			is: true,
			then: (schema) =>
				schema
					.of(
						Validator.object({
							package_range_operator: Validator.string().required(
								"Condition is required"
							),

							package_range_currency: Validator.mixed().required(
								"Currency is required"
							),

							package_range_amount: Validator.number()
								.typeError("Amount must be a number")
								.positive("Amount must be greater than 0")
								.required("Amount is required"),
						})
					)
					.min(1, "At least one package range is required")
					.test(
						"unique-currency",
						"Each currency must be unique",
						(values = []) => {
							const currencies = values
								.map((v) => v?.package_range_currency)
								.filter(Boolean)

							return new Set(currencies).size === currencies.length
						}
					),
			otherwise: (schema) => schema.optional(),
		}),
	}),

	/* ---------------- Minimum first instalment ---------------- */
	min_first_instalment_amount: Validator.object({
		is_include: Validator.boolean().required(),

		values: Validator.array().when("is_include", {
			is: true,
			then: (schema) =>
				schema
					.of(
						Validator.object({
							amount: Validator.number()
								.typeError("Amount must be a number")
								.positive("Amount must be greater than 0")
								.required("Amount is required"),

							currency: Validator.mixed().required("Currency is required"),
						})
					)
					.min(1, "At least one currency is required")
					.test(
						"unique-currency",
						"Each currency must be unique",
						(values = []) => {
							const currencies = values.map((v) => v?.currency).filter(Boolean)

							return new Set(currencies).size === currencies.length
						}
					),
			otherwise: (schema) => schema.nullable(),
		}),
	}),

	/* ---------------- instalment breakdowns ---------------- */
	breakdowns: Validator.array()
		.of(
			Validator.object({
				reference: Validator.mixed().required("Reference is required"),

				day_offset: Validator.number()
					.typeError("Day offset must be a number")
					.integer("Day offset must be an integer")
					.required("Day offset is required"),

				instalment_breakup_in_percent: Validator.number()
					.typeError("Percentage must be a number")
					.integer("Percentage must be an integer")
					.min(1, "Minimum percentage is 1")
					.max(100, "Maximum percentage is 100")
					.required("Percentage is required"),
			})
		)
		.min(1, "At least one instalment is required")
		.test(
			"total-100",
			"Total instalment percentage must be exactly 100%",
			(breakdowns = []) =>
				breakdowns.reduce(
					(sum, b) => sum + (b.instalment_breakup_in_percent || 0),
					0
				) === 100
		),

	/* ---------------- Flags ---------------- */
	is_include_flight_cost_in_first_instalment: Validator.boolean().required(),
})

function includeIf<
	T extends { is_include: boolean },
	R = Omit<T, "is_include">,
>(key: string, obj: T, mapper?: (value: Omit<T, "is_include">) => R) {
	if (!obj.is_include) return {}

	const { is_include, ...rest } = obj
	return { [key]: mapper ? mapper(rest) : rest }
}

const PACKAGE_OPERATOR_MAP: Record<string, number> = {
	is_greater: 1,
	is_less: 0,
}

function transformValues({
	values,
}: {
	values: ICustomerPaymentPreferenceFormValues
}) {
	const payload = {
		...includeIf("days_before_trip_start", values.days_before_trip_start),
		...includeIf("trip_duration", values.trip_duration),
		...includeIf(
			"min_first_instalment_amount",
			values.min_first_instalment_amount,
			({ values }) =>
				values.map((v) => ({ amount: v.amount, currency: v.currency }))
		),
		...includeIf("package_ranges", values.package_ranges, ({ values }) =>
			values.map((v) => ({
				package_range_operator: PACKAGE_OPERATOR_MAP[v.package_range_operator],
				package_range_currency: v.package_range_currency,
				package_range_amount: v.package_range_amount,
			}))
		),
		breakdowns: values.breakdowns.map((b) => ({
			reference: b.reference?.id,
			day_offset: b.day_offset,
			instalment_breakup_in_percent: b.instalment_breakup_in_percent,
		})),

		is_include_flight_cost_in_first_instalment:
			values.is_include_flight_cost_in_first_instalment ? 1 : 0,
	}
	return payload
}

export function NewCustomerPaymentPreference({
	onSuccess,
	onCancel,
}: {
	onSuccess: () => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const { currency: initialCurrency } = useFunctionalCurrencyOfTenant()
	return (
		<Box>
			<Form<ICustomerPaymentPreferenceFormValues>
				onSubmit={withServerErrors(async (values) => {
					const payload = transformValues({ values })
					await xhr.post("/customer-payment-preferences", payload)
					onSuccess?.()
					showSnackbar("Customer payment preference created successfully")
				})}
				initialValues={{
					days_before_trip_start: {
						is_include: false,
						condition: "is_less",
						days: 0,
					},
					trip_duration: {
						is_include: false,
						condition: "is_less",
						nights: 0,
					},
					package_ranges: {
						is_include: false,
						values: [
							{
								package_range_operator: "is_less",
								package_range_currency: initialCurrency,
								package_range_amount: 0,
							},
						],
					},
					breakdowns: [
						{
							reference: undefined,
							day_offset: 0,
							instalment_breakup_in_percent: 50,
						},
					],
					min_first_instalment_amount: {
						is_include: false,
						values: [
							{
								amount: 0,
								currency: initialCurrency,
							},
						],
					},

					is_include_flight_cost_in_first_instalment: false,
				}}
				validate={validateFormValues(customerPaymentPreferenceSchema)}
				mutators={{ ...arrayMutators }}
				subscription={{ submitting: true }}
			>
				{({ handleSubmit, submitting }) => {
					return (
						<form onSubmit={handleSubmit} noValidate>
							<Stack gap={"2"}>
								<Grid gap={"4"}>
									<Col xs={12} md={4}>
										<Stack maxWidth={"xs"}>
											<Text fontSize={"lg"} fontWeight={"semibold"}>
												Define Conditions
											</Text>
											<Text
												fontSize={"base"}
												color={"muted"}
												fontWeight="normal"
											>
												Set conditions to determine when this payment preference
												applies. All selected conditions must be met for this
												preference to be used
											</Text>
										</Stack>
									</Col>
									<Col>
										<Stack gap={"4"}>
											{/* days before trip start  */}
											<Stack gap={"4"}>
												<SwitchInputField
													name="days_before_trip_start.is_include"
													label={"Day before trip start"}
													help="Apply this preference only when booking within a certain number of days before trip start date."
												/>
												<GetFieldValue<ICustomerPaymentPreferenceFormValues> name="days_before_trip_start.is_include">
													{({ value }) =>
														value ? (
															<Inline gap={"4"}>
																<SelectInputField
																	name={"days_before_trip_start.condition"}
																	label="Condition"
																	defaultValue="is_less"
																>
																	<option value="is_less">Is less than</option>
																	<option value="is_greater">
																		Is greater than
																	</option>
																</SelectInputField>
																<Stack>
																	<TextInputField
																		label="Days"
																		name={"days_before_trip_start.days"}
																		required
																		type="number"
																	/>
																</Stack>
															</Inline>
														) : null
													}
												</GetFieldValue>
											</Stack>

											{/* trip duration  */}

											<Stack gap={"4"}>
												<SwitchInputField
													name="trip_duration.is_include"
													label={"Trip Duration (Number of Nights)"}
													help="Apply this preference based on how long the trip lasts. For example: Use different payment terms for trips longer than 7 nights."
												/>
												<GetFieldValue<ICustomerPaymentPreferenceFormValues> name="trip_duration.is_include">
													{({ value }) =>
														value ? (
															<Inline gap={"4"}>
																<SelectInputField
																	name="trip_duration.condition"
																	label="Condition"
																	defaultValue="is_less"
																>
																	<option value="is_less">Is less than</option>
																	<option value="is_greater">
																		Is greater than
																	</option>
																</SelectInputField>
																<Stack>
																	<TextInputField
																		label="Nights"
																		name="trip_duration.nights"
																		required
																		type="number"
																	/>
																</Stack>
															</Inline>
														) : null
													}
												</GetFieldValue>
											</Stack>

											{/* package ranges  */}
											<Stack gap={"4"}>
												<SwitchInputField
													name="package_ranges.is_include"
													label={"Package Amount Range"}
													help="Apply this preference only when the total package value falls above/below specific amount. Useful for setting different payment terms for high-value vs low-value bookings."
												/>
												<GetFieldValue<ICustomerPaymentPreferenceFormValues> name="package_ranges.is_include">
													{({ value }) =>
														value ? (
															<FieldArray name="package_ranges.values">
																{({ fields }) => (
																	<>
																		{fields.map((name, index) => (
																			<Inline gap={"4"} key={index}>
																				<SelectInputField
																					name={`${name}.package_range_operator`}
																					label="Condition"
																					defaultValue="is_less"
																				>
																					<option value="is_less">
																						Is less than
																					</option>
																					<option value="is_greater">
																						Is greater than
																					</option>
																				</SelectInputField>
																				<SelectTenantCurrencyInputField
																					name={`${name}.package_range_currency`}
																					label="Currency"
																					style={{ maxWidth: "150px" }}
																				/>
																				<Inline gap={"4"} alignItems="center">
																					<TextInputField
																						label="Amount"
																						name={`${name}.package_range_amount`}
																						required
																						type="number"
																					/>

																					{Number(fields.length || 0) > 1 ? (
																						<Button
																							onClick={() =>
																								fields.remove(index)
																							}
																							level="tertiary"
																						>
																							<Icons.Cancel />
																						</Button>
																					) : null}
																				</Inline>
																			</Inline>
																		))}
																		<Box>
																			<Button
																				onClick={() =>
																					fields.push({
																						package_range_operator: "is_less",
																						package_range_currency:
																							initialCurrency,
																						package_range_amount: 0,
																					})
																				}
																				size="sm"
																			>
																				<Icons.Plus />
																				Add More
																			</Button>
																		</Box>
																	</>
																)}
															</FieldArray>
														) : null
													}
												</GetFieldValue>
											</Stack>
										</Stack>
									</Col>
								</Grid>

								<Divider />

								{/* instalment breakdown */}

								<Grid gap={"4"}>
									<Col xs={12} md={4}>
										<Stack maxWidth={"xs"}>
											<Text fontSize={"lg"} fontWeight={"semibold"}>
												Instalments Breakdown
											</Text>
											<Text
												fontSize={"base"}
												color={"muted"}
												fontWeight="normal"
											>
												Define the payment instalments for your customers.
												Specify when each payment is due and what percentage of
												the total amount should be paid.
											</Text>
										</Stack>
									</Col>
									<Col>
										<Stack gap={"4"}>
											<Stack>
												<Text fontSize={"md"} fontWeight={"semibold"}>
													Please provide the details for the new payment
													preference that you need for a customer.
												</Text>
												<Text
													fontSize={"sm"}
													color={"muted"}
													fontWeight={"normal"}
												>
													Instalments are calculated from the Trip Conversion
													date (or Trip Hold date if applicable). Use positive
													days for payments after an event and negative days for
													before. Example: '-7' with Trip Start = payment due 7
													days before departure.
												</Text>
											</Stack>
											<SwitchInputField
												name="is_include_flight_cost_in_first_instalment"
												label={"Include flight cost in first instalment"}
												help="When enabled, 100% of flight cost will be added to the first instalment breakdown you set here, and remaining amount will be distributed according to the percentages you set."
											/>

											<Stack gap={"4"}>
												<SwitchInputField
													name="min_first_instalment_amount.is_include"
													label={"Include minimum first instalment amount"}
													help="When enabled, minimum first instalment amount will be added to the first instalment, and remaining amount will be distributed according to the percentages you set."
												/>

												<GetFieldValue<ICustomerPaymentPreferenceFormValues> name="min_first_instalment_amount.is_include">
													{({ value }) =>
														value ? (
															<FieldArray name="min_first_instalment_amount.values">
																{({ fields }) => (
																	<>
																		{fields.map((name, index) => (
																			<Inline gap={"4"} key={index}>
																				<SelectTenantCurrencyInputField
																					name={`${name}.currency`}
																					label="Currency"
																					style={{ maxWidth: "150px" }}
																				/>
																				<TextInputField
																					label="Amount"
																					name={`${name}.amount`}
																					required
																					type="number"
																				/>

																				{Number(fields.length || 0) > 1 ? (
																					<Button
																						onClick={() => fields.remove(index)}
																						level="tertiary"
																					>
																						<Icons.Cancel />
																					</Button>
																				) : null}
																			</Inline>
																		))}
																		<Box>
																			<Button
																				onClick={() =>
																					fields.push({
																						amount: 0,
																						currency: initialCurrency,
																					})
																				}
																				size="sm"
																			>
																				<Icons.Plus />
																				Add More
																			</Button>
																		</Box>
																	</>
																)}
															</FieldArray>
														) : null
													}
												</GetFieldValue>
											</Stack>

											<FieldArray<
												ICustomerPaymentPreferenceFormValues["breakdowns"][number]
											> name="breakdowns">
												{({ fields }) => (
													<Stack as="ol" gap={"2"}>
														{fields.map((name, index) => (
															<Box
																as="li"
																key={name}
																paddingY="2"
																borderBottomWidth="1"
															>
																<Grid gap="4">
																	<Col xs={12} sm>
																		<SelectField
																			select={SelectCustomerPaymentReferences}
																			name={`${name}.reference`}
																			label="Reference Event"
																			fetchOnMount
																			multiple={false}
																			placeholder="Select a reference..."
																		/>
																	</Col>
																	<Col xs={12} sm>
																		<TextInputField
																			label="Day offset from reference"
																			name={`${name}.day_offset`}
																			required
																			type="number"
																		/>
																	</Col>
																	<Col xs={12} sm style={{ minWidth: "220px" }}>
																		<PercentageInputField
																			label="Amount share from total amount"
																			name={`${name}.instalment_breakup_in_percent`}
																			required
																			type="number"
																			min={1}
																			max={100}
																		/>
																	</Col>
																	<Col xs={12} sm="auto">
																		{Number(fields.length) > 1 ? (
																			<Button
																				level="tertiary"
																				onClick={() => fields.remove(index)}
																				size="sm"
																			>
																				<Icons.Cancel /> Remove
																			</Button>
																		) : null}
																	</Col>
																</Grid>
															</Box>
														))}
														<GetFieldValue<
															ICustomerPaymentPreferenceFormValues["breakdowns"]
														> name="breakdowns">
															{({ value: breakdowns }) => {
																const totalAmountShare =
																	getTotalAmountShare(breakdowns)
																return totalAmountShare < 100 ? (
																	<Box as="li" key="add_more">
																		<Button
																			size="sm"
																			onClick={() =>
																				fields.push({
																					instalment_breakup_in_percent:
																						100 - totalAmountShare,
																					day_offset: 0,
																				})
																			}
																		>
																			<Icons.Plus /> Add More
																		</Button>
																	</Box>
																) : null
															}}
														</GetFieldValue>
													</Stack>
												)}
											</FieldArray>
										</Stack>
									</Col>
								</Grid>

								<Box>
									<GetFieldValue<
										ICustomerPaymentPreferenceFormValues["breakdowns"]
									> name="breakdowns">
										{({ value: breakdowns }) => (
											<PreferenceName breakdowns={breakdowns} />
										)}
									</GetFieldValue>
								</Box>

								<SubmissionError />

								<Divider />

								<Inline gap="2">
									<Button type="submit" level="primary" disabled={submitting}>
										{submitting ? "Saving..." : "Save Preference"}
									</Button>
									<Button type="button" level="tertiary" onClick={onCancel}>
										Cancel
									</Button>
								</Inline>
							</Stack>
						</form>
					)
				}}
			</Form>
		</Box>
	)
}
function getBreakdownName(
	breakdown: ICustomerPaymentPreferenceFormValues["breakdowns"][number]
): React.ReactNode {
	const { reference, day_offset, instalment_breakup_in_percent } = breakdown
	if (!reference || isNaN(day_offset) || isNaN(instalment_breakup_in_percent))
		return null
	return (
		<Box display="inline">
			<span>{instalment_breakup_in_percent}%</span>{" "}
			{day_offset !== 0 ? (
				<span>{pluralize("day", Math.abs(day_offset), true)}</span>
			) : null}{" "}
			<em>{day_offset < 0 ? " before" : day_offset > 0 ? " after" : " on"}</em>{" "}
			<span>{reference.name}</span>
		</Box>
	)
}

export function PreferenceName({
	breakdowns,
}: {
	breakdowns: ICustomerPaymentPreferenceFormValues["breakdowns"]
}) {
	const parts = breakdowns.map(getBreakdownName).filter((i) => i)
	if (!parts.length) return null
	return (
		<Box
			boxShadow="lg"
			rounded="lg"
			borderWidth="1"
			padding="4"
			bgColor="inset"
		>
			<Box
				textTransform="uppercase"
				fontWeight="semibold"
				letterSpacing="wider"
				color="muted"
				fontSize="sm"
			>
				Summary
			</Box>
			<Box fontWeight="semibold" fontSize="xl">
				{joinAttributes(...parts)}
			</Box>
		</Box>
	)
}

function SegmentTooltipContent({
	color,
	children,
}: {
	color: string
	children: React.ReactNode
}) {
	return (
		<Inline gap="2" alignItems="center">
			<Box
				size="3"
				rounded="full"
				flexShrink="0"
				style={{ backgroundColor: color }}
			/>
			<Box whiteSpace="nowrap">{children}</Box>
		</Inline>
	)
}

export function InstalmentShareBar({
	breakdowns,
	height = "3",
	referenceNames,
}: {
	breakdowns?: Array<{
		instalment_breakup_in_percent?: number
		reference?: number | string
		reference_label?: string
		day_offset?: number
	}>
	height?: React.ComponentProps<typeof Box>["height"]
	// Only needed when the breakdowns don't carry a `reference_label`, e.g. the
	// unsaved instalments of the rule form
	referenceNames?: Record<number, string>
}) {
	const segments = getShareSegments(breakdowns, referenceNames)
	if (!segments.length) return null

	return (
		<Stack gap="1">
			<Inline height={height} gap="px" overflow="hidden" rounded="md">
				{segments.map((segment, index) => {
					const label =
						formatInstalmentLabel({
							share: segment.share,
							dayOffset: segment.dayOffset,
							referenceName: segment.referenceName,
						}) || `${segment.share}%`

					return (
						<Tooltip
							key={index}
							content={
								<SegmentTooltipContent color={segment.color}>
									{label}
								</SegmentTooltipContent>
							}
						>
							<Box
								height="full"
								style={{
									flex: `${segment.share} 1 0%`,
									minWidth: 0,
									backgroundColor: segment.color,
								}}
								roundedLeft={index === 0 ? "md" : undefined}
								roundedRight={index === segments.length - 1 ? "md" : undefined}
							/>
						</Tooltip>
					)
				})}
			</Inline>
			<Inline gap="px">
				{segments.map((segment, index) => {
					return (
						<Box
							key={index}
							style={{ flex: `${segment.share} 1 0%`, minWidth: 0 }}
						>
							<Inline
								gap="1"
								alignItems="center"
								justifyContent="center"
								flexWrap="nowrap"
							>
								<Text
									fontSize="sm"
									color="muted"
									fontWeight="semibold"
									whiteSpace="nowrap"
								>
									{segment.share}%
								</Text>
								{segment.showArrow ? (
									<Icons.ArrowRightFromLine color="danger" size="4" />
								) : null}
							</Inline>
						</Box>
					)
				})}
			</Inline>
		</Stack>
	)
}

export type TPaymentRuleFormValues = {
	condition_type: "fallback" | "specific"
	days_before_trip_start: {
		is_include: boolean
		condition: string
		days: number
	}
	trip_duration: {
		is_include: boolean
		condition: string
		nights: number
	}
	min_first_instalment_amount: {
		is_include: boolean
		values: { amount: number; currency: TCurrencyCode }[]
	}
	breakdowns: {
		reference?: string
		day_offset: number
		instalment_breakup_in_percent: number
	}[]
}

function getInitialValues(currency: TCurrencyCode): TPaymentRuleFormValues {
	return {
		condition_type: "fallback",
		days_before_trip_start: {
			is_include: false,
			condition: "is_less",
			days: 0,
		},
		trip_duration: { is_include: false, condition: "is_less", nights: 0 },
		min_first_instalment_amount: {
			is_include: false,
			values: [{ amount: 0, currency }],
		},
		breakdowns: [
			{
				reference: undefined,
				day_offset: 0,
				instalment_breakup_in_percent: 100,
			},
		],
	}
}

const validationSchema = Validator.object().shape({
	condition_type: Validator.string()
		.oneOf(["fallback", "specific"])
		.required("Please select when this rule applies")
		.test(
			"has-a-condition",
			"Please select at least one condition, or make this a fallback rule",
			function (conditionType) {
				if (conditionType !== "specific") return true
				const values = this.parent as TPaymentRuleFormValues
				return Boolean(
					values.days_before_trip_start?.is_include ||
					values.trip_duration?.is_include
				)
			}
		),
	days_before_trip_start: Validator.object().shape({
		is_include: Validator.boolean().required(),
		condition: Validator.string().optional(),
		days: Validator.number().when("is_include", {
			is: true,
			then: (s) =>
				s
					.integer("Days must be a whole number")
					.min(0, "Must be ≥ 0")
					.max(366, "Must be ≤ 366")
					.required("Required"),
			otherwise: (s) => s.optional(),
		}),
	}),
	trip_duration: Validator.object().shape({
		is_include: Validator.boolean().required(),
		condition: Validator.string().optional(),
		nights: Validator.number().when("is_include", {
			is: true,
			then: (s) =>
				s
					.integer("Nights must be a whole number")
					.min(0, "Must be ≥ 0")
					.max(366, "Must be ≤ 366")
					.required("Required"),
			otherwise: (s) => s.optional(),
		}),
	}),
	min_first_instalment_amount: Validator.object().shape({
		is_include: Validator.boolean().required(),
		values: Validator.array().when("is_include", {
			is: true,
			then: (schema) =>
				schema
					.of(
						Validator.object().shape({
							amount: Validator.number()
								.positive("Must be > 0")
								.required("Required"),
							currency: Validator.mixed().required("Currency is required"),
						})
					)
					.min(1, "At least one currency is required")
					.test("unique-currency", "Each currency must be unique", (values) => {
						const currencies = (values || [])
							.map((v) => v?.currency)
							.filter(Boolean)
						return new Set(currencies).size === currencies.length
					}),
			otherwise: (schema) => schema.optional(),
		}),
	}),
	breakdowns: Validator.array()
		.of(
			Validator.object().shape({
				reference: Validator.string().required("Reference event is required"),
				day_offset: Validator.number()
					.integer("Days must be a whole number")
					.min(-60, "Must be ≥ -60")
					.max(60, "Must be ≤ 60")
					.required("Required"),
				instalment_breakup_in_percent: Validator.number()
					.min(1, "Minimum share is 1%")
					.max(100, "Maximum share is 100%")
					.required("Required"),
			})
		)
		.min(1, "Add at least one instalment")
		.test(
			"total-100",
			"Total instalment share must be exactly 100%",
			(breakdowns) => getTotalInstalmentShare(breakdowns) === 100
		),
})

export function NewPaymentRuleForm({
	onSuccess,
	onCancel,
}: {
	onSuccess: (rule: TCustomerPaymentPreference) => void
	onCancel?: () => void
}) {
	const xhr = useXHR()
	const { currency } = useFunctionalCurrencyOfTenant()
	return (
		<Form<TPaymentRuleFormValues>
			initialValues={getInitialValues(currency)}
			validate={validateFormValues(validationSchema)}
			mutators={{ ...arrayMutators }}
			subscription={{ submitting: true }}
			onSubmit={withServerErrors(async (values: TPaymentRuleFormValues) => {
				const rule = await customerPaymentPreferencesXHR(xhr).create(
					ruleFormValuesToPayload(values)
				)
				onSuccess(rule)
			})}
		>
			{({ form, handleSubmit, submitting }) => {
				return (
					<form
						noValidate
						onSubmit={(e) => {
							// this form is nested inside the policy form
							e.stopPropagation()
							handleSubmit(e)
						}}
					>
						<Dialog.Body paddingX="0">
							<Stack>
								<Stack gap="4" paddingX="6">
									<Text fontSize="lg" fontWeight="semibold">
										Condition
									</Text>
									<Box maxWidth="xs">
										<SelectInputField name="condition_type" hideErrors>
											<option value="fallback">Fallback Rule</option>
											<option value="specific">
												Applies on specific conditions
											</option>
										</SelectInputField>
									</Box>
									<ResetConditionsForFallbackRule />
									<GetFieldValue<
										TPaymentRuleFormValues["condition_type"]
									> name="condition_type">
										{({ value }) =>
											value === "specific" ? <ConditionFields /> : null
										}
									</GetFieldValue>
									<Field name="condition_type">
										{({ meta }) =>
											meta.error ? (
												<Inline
													alignItems="center"
													gap="2"
													paddingY="1"
													paddingX="2"
													color="white"
													borderColor="danger"
													backgroundColor="danger_emphasis"
													borderWidth="1"
													rounded="md"
												>
													<Text fontSize="sm">{meta.error}</Text>
												</Inline>
											) : null
										}
									</Field>
								</Stack>
								<Divider marginY="6" />
								<InstalmentFields />
								<Stack gap="4" paddingX="6" paddingY="4">
									<CheckboxInputField
										name="min_first_instalment_amount.is_include"
										label="Set Minimum amount of the first instalment"
									/>
									<GetFieldValue<boolean> name="min_first_instalment_amount.is_include">
										{({ value }) => (value ? <MinimumAmountFields /> : null)}
									</GetFieldValue>
								</Stack>
								<GetFieldValue<
									TPaymentRuleFormValues["breakdowns"]
								> name="breakdowns">
									{({ value: breakdowns }) => (
										<GetFieldValue<
											TPaymentRuleFormValues["min_first_instalment_amount"]
										> name="min_first_instalment_amount">
											{({ value: minAmounts }) => {
												const formattedMinAmounts =
													formatFormMinFirstInstalmentAmounts(minAmounts)
												return (
													<Box paddingX="6">
														<RuleSummary
															breakdowns={breakdowns}
															minAmounts={formattedMinAmounts}
														/>
													</Box>
												)
											}}
										</GetFieldValue>
									)}
								</GetFieldValue>
								<Box paddingX="6" paddingY="4">
									<SubmissionError />
								</Box>
							</Stack>
						</Dialog.Body>
						<Dialog.Footer>
							<Inline gap="4">
								<Button
									type="submit"
									level="primary"
									status="primary"
									disabled={submitting}
								>
									{submitting ? "Creating..." : "Create rule"}
								</Button>
								{onCancel ? (
									<Button
										level="tertiary"
										onClick={onCancel}
										disabled={submitting}
									>
										Cancel
									</Button>
								) : null}
							</Inline>
						</Dialog.Footer>
					</form>
				)
			}}
		</Form>
	)
}

// The conditions stay in the form values when the rule is switched back to
// "Fallback Rule", so they are unchecked to keep them out of the payload and
// out of the validation.
function ResetConditionsForFallbackRule() {
	const form = useForm()
	const { value: conditionType } =
		useFieldValue<TPaymentRuleFormValues["condition_type"]>("condition_type")
	useEffect(() => {
		if (conditionType !== "fallback") return
		form.batch(() => {
			form.change("days_before_trip_start.is_include", false)
			form.change("trip_duration.is_include", false)
		})
	}, [conditionType, form])
	return null
}

// Every condition is optional on its own: a rule applies when all the checked
// conditions are met. Adding a new condition type is adding a row here.
function ConditionFields() {
	return (
		<Box
			as="table"
			style={{ borderCollapse: "collapse", width: "fit-content" }}
		>
			<Box as="tbody">
				<ConditionRow
					name="days_before_trip_start"
					label="Days until Check-in"
					valueName="days"
					valueLabel="Days"
					hasBorderBottom
				/>
				<ConditionRow
					name="trip_duration"
					label="Stay Duration (Number of Nights)"
					valueName="nights"
					valueLabel="Nights"
				/>
			</Box>
		</Box>
	)
}

function ConditionRow({
	name,
	label,
	valueName,
	valueLabel,
	hasBorderBottom,
}: {
	name: string
	label: string
	valueName: string
	valueLabel: string
	hasBorderBottom?: boolean
}) {
	return (
		<Box as="tr">
			<Box
				as="td"
				paddingY="2"
				paddingRight="4"
				verticalAlign="middle"
				borderBottomWidth={hasBorderBottom ? "1" : undefined}
			>
				<CheckboxInputField
					name={`${name}.is_include`}
					label={<Text fontWeight="semibold">{label}</Text>}
				/>
			</Box>
			<GetFieldValue<boolean> name={`${name}.is_include`}>
				{({ value }) =>
					value ? (
						<>
							<Box
								as="td"
								paddingY="2"
								paddingRight="3"
								verticalAlign="middle"
								borderBottomWidth={hasBorderBottom ? "1" : undefined}
								style={{ width: "1%", whiteSpace: "nowrap" }}
							>
								<SelectInputField
									name={`${name}.condition`}
									borderWidth="0"
									style={{
										backgroundColor: "transparent",
										cursor: "pointer",
										outline: "none",
									}}
								>
									<option value="is_less">Is less than</option>
									<option value="is_greater">Is greater than</option>
								</SelectInputField>
							</Box>
							<Box
								as="td"
								paddingY="2"
								paddingRight="2"
								verticalAlign="middle"
								borderBottomWidth={hasBorderBottom ? "1" : undefined}
								style={{ width: "5.5rem" }}
							>
								<Box style={{ width: "5rem" }}>
									<TextInputField
										name={`${name}.${valueName}`}
										type="number"
										min={0}
										className={noSpinnersClassName}
									/>
								</Box>
							</Box>
							<Box
								as="td"
								paddingY="2"
								verticalAlign="middle"
								borderBottomWidth={hasBorderBottom ? "1" : undefined}
								style={{ width: "1%", whiteSpace: "nowrap" }}
							>
								<Text fontWeight="semibold">{valueLabel}</Text>
							</Box>
						</>
					) : (
						<Box
							as="td"
							colSpan={3}
							paddingY="2"
							borderBottomWidth={hasBorderBottom ? "1" : undefined}
						/>
					)
				}
			</GetFieldValue>
		</Box>
	)
}

function InstalmentFields() {
	const { references, isLoading } = useCustomerPaymentReferences()
	return (
		<Box paddingX="6">
			<FieldArray<
				TPaymentRuleFormValues["breakdowns"][number]
			> name="breakdowns">
				{({ fields }) => (
					<Stack gap="4" paddingBottom="4">
						<Box
							rounded="md"
							borderWidth="1"
							overflow="auto"
							maxWidth="full"
							style={{ width: "fit-content" }}
						>
							<Box as="table">
								<Box as="thead">
									<Box as="tr" bgColor="subtle">
										{["Share", "", "Reference Event", ""].map((header, i) => (
											<Box
												as="th"
												key={i}
												padding="3"
												textAlign="left"
												fontSize="sm"
												fontWeight="semibold"
												color="muted"
												borderBottomWidth="1"
											>
												{header}
											</Box>
										))}
									</Box>
								</Box>
								<Box as="tbody">
									{fields.map((name, index) => (
										<Box as="tr" key={name} borderBottomWidth="1">
											<Box as="td" padding="3">
												<PercentageInputField
													name={`${name}.instalment_breakup_in_percent`}
													min={1}
													className={noSpinnersClassName}
												/>
											</Box>
											<Box as="td" padding="3" paddingLeft="4">
												<DayOffsetField name={`${name}.day_offset`} />
											</Box>
											<Box as="td" padding="3" width="16">
												<SelectInputField
													name={`${name}.reference`}
													disabled={isLoading}
													borderWidth="0"
													style={{
														backgroundColor: "transparent",
														cursor: "pointer",
														outline: "none",
													}}
												>
													<option value="">Select a reference...</option>
													{references.map((ref) => (
														<option key={ref.id} value={ref.id}>
															{ref.name}
														</option>
													))}
												</SelectInputField>
											</Box>
											<Box as="td" padding="3">
												{fields.length && fields.length > 1 ? (
													<Button
														level="tertiary"
														size="sm"
														onClick={() => fields.remove(index)}
														title="Remove this instalment"
													>
														<Icons.Cancel />
													</Button>
												) : null}
											</Box>
										</Box>
									))}
								</Box>
							</Box>
						</Box>
						<GetFieldValue<
							TPaymentRuleFormValues["breakdowns"]
						> name="breakdowns">
							{({ value: breakdowns }) => {
								const totalShare = getTotalInstalmentShare(breakdowns)
								return totalShare < 100 ? (
									<Inline>
										<Button
											level="secondary"
											size="sm"
											onClick={() =>
												fields.push({
													reference: undefined,
													day_offset: 0,
													instalment_breakup_in_percent:
														Math.round((100 - totalShare) * 100) / 100,
												})
											}
										>
											<Icons.Plus /> Add Installment
										</Button>
									</Inline>
								) : null
							}}
						</GetFieldValue>
					</Stack>
				)}
			</FieldArray>
		</Box>
	)
}

// `day_offset` is one signed number, but it reads as a sentence, so it is shown
// as text with a pencil to edit the number.
function DayOffsetField({ name }: { name: string }) {
	const form = useForm()
	const [editing, setEditing] = useState(false)
	return (
		<GetFieldValue<number> name={name}>
			{({ value }) => (
				<Inline gap="1" alignItems="center" justifyContent="end">
					{editing ? (
						<DayOffsetEditor
							dayOffset={typeof value === "number" ? value : Number(value || 0)}
							onChange={(nextOffset) => {
								form.change(name, nextOffset)
								setEditing(false)
							}}
						/>
					) : (
						<>
							<Text whiteSpace="preserve" color="muted">
								{formatDayOffsetLabel(Number(value || 0))}
							</Text>
							<Button
								inline
								onClick={() => setEditing(true)}
								title="Edit the due date"
							>
								<Icons.Pencil />
							</Button>
						</>
					)}
				</Inline>
			)}
		</GetFieldValue>
	)
}

function DayOffsetEditor({
	dayOffset,
	onChange,
}: {
	dayOffset: number
	onChange: (dayOffset: number) => void
}) {
	const [days, setDays] = useState<string>(String(dayOffset))
	function apply() {
		const value = Number(days)
		onChange(isNaN(value) ? 0 : Math.round(value))
	}
	return (
		<Box style={{ maxWidth: "3.5rem" }}>
			<Input
				type="number"
				value={days}
				autoFocus
				onFocus={(e) => e.currentTarget.select()}
				onChange={(e) => {
					const value = Number(e.currentTarget.value)
					if (value > 60 || value < -60) return
					setDays(e.currentTarget.value)
				}}
				onBlur={apply}
				onKeyDown={(e) => {
					if (e.key === "Enter") {
						e.currentTarget.blur()
					}
				}}
				className={noSpinnersClassName}
			/>
		</Box>
	)
}

function MinimumAmountFields() {
	const currencies = useCurrenciesOfTenant()

	return (
		<FieldArray<
			TPaymentRuleFormValues["min_first_instalment_amount"]["values"][number]
		>
			name="min_first_instalment_amount.values"
			subscription={{ value: true }}
		>
			{({ fields }) => {
				const selectedCurrencies = (fields.value || []).map((v) => v.currency)
				const nextAvailableCurrency = currencies.find(
					(c) => !selectedCurrencies.includes(c.currency)
				)?.currency

				return (
					<Stack gap="2">
						{fields.map((name, index) => {
							const otherSelected = selectedCurrencies.filter(
								(_, i) => i !== index
							)

							return (
								<Inline gap="2" key={name} alignItems="start">
									<SelectTenantCurrencyInputField
										name={`${name}.currency`}
										disabledCurrencies={otherSelected}
										borderWidth="0"
										style={{
											backgroundColor: "transparent",
											outline: "none",
											cursor: currencies.length > 1 ? "pointer" : "default",
										}}
									/>
									<TextInputField
										name={`${name}.amount`}
										type="number"
										min={0}
										className={noSpinnersClassName}
										width="20"
									/>
									{fields.length && fields.length > 1 ? (
										<Box paddingBottom="1">
											<Button
												level="tertiary"
												onClick={() => fields.remove(index)}
											>
												<Icons.Cancel />
											</Button>
										</Box>
									) : null}
								</Inline>
							)
						})}
						{!nextAvailableCurrency ? null : (
							<Box>
								<Button
									size="sm"
									disabled={!nextAvailableCurrency}
									onClick={() =>
										fields.push({
											currency: nextAvailableCurrency,
											amount: 0,
										})
									}
								>
									<Icons.Plus /> Add another currency
								</Button>
							</Box>
						)}
					</Stack>
				)
			}}
		</FieldArray>
	)
}

export function RuleSummary({
	breakdowns,
	referenceNames: referenceNamesProp,
	minAmounts,
}: {
	breakdowns?: TPaymentRuleFormValues["breakdowns"]
	referenceNames?: Record<number, string>
	minAmounts?: TCustomerPaymentPreference["min_first_instalment_amounts"]
}) {
	const { referenceNames: fetchedNames } = useCustomerPaymentReferences()
	const referenceNames = referenceNamesProp || fetchedNames
	const labels = formatFormInstalmentLabels(breakdowns || [], referenceNames)
	if (!labels.length) return null
	return (
		<Box rounded="lg" borderWidth="1" padding="4" bgColor="inset">
			<Stack gap="3">
				<Text
					textTransform="uppercase"
					fontWeight="semibold"
					letterSpacing="wider"
					color="muted"
					fontSize="sm"
				>
					Summary
				</Text>
				<Text fontWeight="semibold">{joinAttributes(...labels)}</Text>
				<MinimumAmountBadge value={minAmounts} />
				<InstalmentShareBar
					breakdowns={breakdowns}
					referenceNames={referenceNames}
				/>
			</Stack>
		</Box>
	)
}

export function NewPaymentRuleInDialog({
	children,
	onSuccess,
}: {
	children: (props: { onClick: () => void }) => React.ReactNode
	onSuccess: (rule: TCustomerPaymentPreference) => void
}) {
	const [isOpen, open, close] = useDialog()
	return (
		<>
			{children({ onClick: open })}
			<Dialog open={isOpen} onClose={close} title="Create New Rule" md>
				<NewPaymentRuleForm
					onSuccess={(rule) => {
						close()
						onSuccess(rule)
					}}
					onCancel={close}
				/>
			</Dialog>
		</>
	)
}

export function MinimumAmountBadge({
	value,
}: {
	value?: TCustomerPaymentPreference["min_first_instalment_amounts"]
}) {
	if (!value?.length) return null
	return (
		<Inline gap="2" color="muted">
			Minimum Amount:
			<Inline gap="px">
				{joinAttributes(
					...value.map(({ amount }, index) => (
						<Money
							key={index}
							amount={Number(formatMoneyByDecimal(amount))}
							currency={amount.currency}
							showCurrency
						/>
					))
				)}
			</Inline>
		</Inline>
	)
}
